import { Component, Input } from '@angular/core';
import { Chart } from 'chart.js';
import { ActivatedRoute } from '@angular/router';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { DatePipe } from '@angular/common';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Subject, finalize, takeUntil } from 'rxjs';
import { BranchSelectionService } from '../../../Services/branch-selection.service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';

@Component({
    selector: 'app-api-details-page',
    templateUrl: './api-details-page.component.html',
    styleUrls: ['./api-details-page.component.scss'],
    animations: [
        trigger('slideDownInOut', [
            state('void', style({
                opacity: 0,
                transform: 'translateY(-10%)',
            })),
            transition('void => *', animate('600ms ease-out')),
            transition('* => void', animate('600ms ease-in')),
        ]),
    ],
    standalone: false
})
export class ApiDetailsPageComponent {
  private chart: Chart | null = null;
  integrationName: string | null = null;
  productType: string | null = null;
  anniversaryDate: string | null = null;
  totalActiveCredentials: number | null = null;
  chartData: { label: string; value: number }[] = [];
  private destroy$ = new Subject<void>();
  branchId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    public backend: BackendAccessService,
    public datePipe: DatePipe,
    private branchSelectionService: BranchSelectionService,
    private loadingService: LoadingService
  ) {
    this.handleResize = this.handleResize.bind(this);
  }
  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      console.log('Query Params:', params);
      this.integrationName = params['integrationName'];
      this.productType = params['productType'];
      this.totalActiveCredentials = params['totalActiveCredentials'];
      this.branchId! = params['branchId'];
    });

    // Listen for branch selections
    this.branchSelectionService.selectedBranch$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedBranch) => {
        console.log('Selected branch:', selectedBranch);
        // If a branch is explicitly selected, use it
        if (selectedBranch) {
          this.loadAPICredentialDetails(selectedBranch);
        } else {
          // Otherwise, use the branchId from the queryParams if available
          this.branchId !== null &&
            this.loadAPICredentialDetails(this.branchId);
        }
      });

    // If there's a branchId in queryParams and no branch is selected initially, load details for it
    if (
      this.branchId !== null &&
      !this.branchSelectionService.getSelectedBranch()
    ) {
      this.loadAPICredentialDetails(this.branchId);
      console.log('hit this');
    }
  }

  loadAPICredentialDetails(branchId?: string) {
    // If no branchId is provided, try getting the selected branch
    const effectiveBranchId =
      branchId || this.branchSelectionService.getSelectedBranch();
    if (!effectiveBranchId) {
      console.error('No branchId provided and no branch selected.');
      return;
    }
    this.loadingService.show();

    // The rest of the API call logic now uses effectiveBranchId
    const integrationId = this.route.snapshot.paramMap.get('integrationId');
    const skuCode = this.route.snapshot.queryParamMap.get('skuCode');

    if (integrationId && skuCode) {
      this.backend
        .getAPICredentialDetailsData(integrationId, skuCode, effectiveBranchId)
        .pipe(finalize(() => this.loadingService.hide()))
        .subscribe(
          (data) => {
            console.log('API Details Data', data);
            this.anniversaryDate = data.anniversaryDate;
            this.chartData = data.monthlyData.map((item: any) => ({
              label: `${item.createdMonth} ${item.createdYear}`,
              value: item.totalActiveCredentials,
            }));
            this.createChart();
          },
          (error) => {
            console.error('Error fetching API details data:', error);
          }
        );
    } else {
      console.error(
        'Integration ID or SKU Code is missing from the route parameters.'
      );
    }
  }

  ngAfterViewInit() {
    window.addEventListener('resize', this.handleResize);
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleResize() {
    if (this.chart) {
      this.chart.resize();
    }
  }

  createChart() {
    const canvas = document.getElementById('APIChart') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const labels = this.chartData.map((dataItem) => dataItem.label);
    const singleMonth = labels.length === 1;

    this.chart = new Chart(ctx, {
      type: 'line',

      data: {
        labels: singleMonth ? ['', ...labels, ''] : [...labels],
        datasets: [
          {
            label: 'Number of Active Credentials',
            data: singleMonth
              ? [0, ...this.chartData.map((dataItem) => dataItem.value)]
              : [...this.chartData.map((dataItem) => dataItem.value)],
            backgroundColor: 'rgba(29, 78, 216, 0.2)',
            borderColor: 'rgba(29, 78, 216, 2)',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
              },
            },
          ],
        },
      },
    });
  }
}
